import React, { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Eye, EyeOff, Image as ImageIcon, Star } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errMsg } from "@/lib/api";
import { AdminCard, PageTitle } from "@/pages/admin/AdminLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { LANG_LABELS } from "@/i18n/strings";

const inputCls = "w-full h-11 px-4 rounded-xl border border-sand bg-white text-sm text-ocean focus:outline-none focus:border-sea";
const textCls = "w-full px-4 py-3 rounded-xl border border-sand bg-white text-sm text-ocean focus:outline-none focus:border-sea";

const LANGS = ["en", "it", "es", "fr"];

const emptyTr = { title: "", excerpt: "", content: "" };
const emptyPost = () => ({
  slug: "",
  image: "",
  tags: [],
  published: true,
  featured: false,
  order: 0,
  translations: { en: { ...emptyTr }, it: { ...emptyTr }, es: { ...emptyTr }, fr: { ...emptyTr } },
});

const F = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="block text-[11px] tracking-[0.18em] uppercase text-ocean/55 mb-2">{label}</label>
    {children}
  </div>
);

export default function AdminDiary() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editLang, setEditLang] = useState("it");

  const load = () => adminApi.get("/admin/diary").then((r) => setList(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const payload = (p) => ({
    slug: p.slug.trim(),
    image: p.image.trim(),
    tags: Array.isArray(p.tags) ? p.tags : String(p.tags || "").split(",").map((x) => x.trim()).filter(Boolean),
    published: !!p.published,
    featured: !!p.featured,
    order: Number(p.order) || 0,
    translations: p.translations,
  });

  const save = async () => {
    if (!edit.slug.trim()) return toast.error("Slug obbligatorio");
    if (!edit.translations.en.title.trim() || !edit.translations.en.content.trim())
      return toast.error("Titolo e contenuto in inglese sono obbligatori");
    setSaving(true);
    try {
      if (edit.id) await adminApi.put(`/admin/diary/${edit.id}`, payload(edit));
      else await adminApi.post("/admin/diary", payload(edit));
      toast.success("Articolo salvato");
      setEdit(null);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare l'articolo "${p.translations?.it?.title || p.slug}"?`)) return;
    try {
      await adminApi.delete(`/admin/diary/${p.id}`);
      setList((l) => l.filter((x) => x.id !== p.id));
      toast.success("Articolo eliminato");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const togglePub = async (p) => {
    try {
      await adminApi.put(`/admin/diary/${p.id}`, payload({ ...p, published: !p.published }));
      setList((l) => l.map((x) => (x.id === p.id ? { ...x, published: !p.published } : x)));
      toast.success(p.published ? "Articolo nascosto" : "Articolo pubblicato");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openEdit = (p) => {
    const tr = p.translations || {};
    const merged = {
      ...p,
      tags: Array.isArray(p.tags) ? p.tags.join(", ") : (p.tags || ""),
      translations: {
        en: { ...emptyTr, ...(tr.en || {}) },
        it: { ...emptyTr, ...(tr.it || {}) },
        es: { ...emptyTr, ...(tr.es || {}) },
        fr: { ...emptyTr, ...(tr.fr || {}) },
      },
    };
    setEdit(merged);
    setEditLang("it");
  };

  const setTr = (lang, field, value) =>
    setEdit((e) => ({ ...e, translations: { ...e.translations, [lang]: { ...e.translations[lang], [field]: value } } }));

  const sorted = useMemo(() => [...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [list]);

  return (
    <div data-testid="admin-diary">
      <PageTitle
        title="Diario di viaggio"
        sub="Articoli brevi del blog. Usa immagini della libreria (percorso /images/...)."
        right={
          <button
            onClick={() => { setEdit(emptyPost()); setEditLang("it"); }}
            className="inline-flex items-center gap-2 px-5 py-3 bg-ocean text-ivory text-sm font-medium rounded-xl btn-lift"
            data-testid="admin-diary-new"
          >
            <Plus size={16} />Nuovo articolo
          </button>
        }
      />

      {loading && <AdminCard className="p-8 text-center text-ocean/45">Caricamento...</AdminCard>}
      {!loading && sorted.length === 0 && (
        <AdminCard className="p-10 text-center text-ocean/50" data-testid="admin-diary-empty">
          Nessun articolo. Clicca "Nuovo articolo" per iniziare.
        </AdminCard>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {sorted.map((p) => {
          const tr = p.translations?.it?.title ? p.translations.it : (p.translations?.en || {});
          return (
            <AdminCard key={p.id} className="p-0 overflow-hidden" data-testid={`admin-diary-${p.slug}`}>
              <div className="relative aspect-[16/10] bg-sand/40">
                {p.image ? (
                  <img src={p.image} alt={tr.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-ocean/30"><ImageIcon size={32} /></div>
                )}
                <div className="absolute top-3 left-3 flex gap-2">
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium border ${p.published ? "bg-sage-mist text-sage-dark border-sage-light" : "bg-amber-50 text-amber-800 border-amber-200"}`}>
                    {p.published ? "Pubblicato" : "Bozza"}
                  </span>
                  {p.featured && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium bg-ocean text-ivory">
                      <Star size={10} /> In evidenza
                    </span>
                  )}
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-serif text-lg text-ocean leading-snug">{tr.title || p.slug}</h3>
                <p className="mt-2 text-xs text-ocean/60 line-clamp-2">{tr.excerpt}</p>
                <p className="mt-2 text-[10px] tracking-[0.2em] uppercase text-ocean/40">
                  /{p.slug}{p.tags?.length ? ` · ${p.tags.join(", ")}` : ""}
                </p>
                <div className="mt-4 flex items-center gap-2">
                  <button
                    onClick={() => togglePub(p)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-sand text-ocean text-xs hover:bg-sand/40"
                    data-testid={`admin-diary-toggle-${p.slug}`}
                  >
                    {p.published ? <><EyeOff size={14} />Nascondi</> : <><Eye size={14} />Pubblica</>}
                  </button>
                  <button
                    onClick={() => openEdit(p)}
                    aria-label="Modifica"
                    className="p-2 text-ocean/60 hover:text-ocean"
                    data-testid={`admin-diary-edit-${p.slug}`}
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => remove(p)}
                    aria-label="Elimina"
                    className="p-2 text-rose-700"
                    data-testid={`admin-diary-delete-${p.slug}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </AdminCard>
          );
        })}
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-white sm:max-w-3xl rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-diary-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-ocean">{edit?.id ? "Modifica articolo" : "Nuovo articolo"}</DialogTitle>
            <DialogDescription>Compila almeno la versione inglese; le altre lingue mostrano l'inglese come fallback.</DialogDescription>
          </DialogHeader>

          {edit && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <F label="Slug (URL)">
                  <input
                    className={inputCls}
                    value={edit.slug}
                    onChange={(e) => setEdit({ ...edit, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
                    data-testid="admin-diary-slug"
                    placeholder="es. una-tavola-sul-mare"
                  />
                </F>
                <F label="Ordine (numero)">
                  <input type="number" className={inputCls} value={edit.order} onChange={(e) => setEdit({ ...edit, order: Number(e.target.value) })} data-testid="admin-diary-order" />
                </F>
                <F label="Immagine (percorso)" className="md:col-span-2">
                  <input
                    className={inputCls}
                    value={edit.image}
                    onChange={(e) => setEdit({ ...edit, image: e.target.value })}
                    placeholder="/images/09_napoli_pizza_concept.webp"
                    data-testid="admin-diary-image"
                  />
                  {edit.image && (
                    <div className="mt-3 aspect-[16/9] overflow-hidden rounded-xl border border-sand">
                      <img src={edit.image} alt="preview" className="w-full h-full object-cover" />
                    </div>
                  )}
                </F>
                <F label="Tag (separati da virgola)" className="md:col-span-2">
                  <input
                    className={inputCls}
                    value={edit.tags}
                    onChange={(e) => setEdit({ ...edit, tags: e.target.value })}
                    placeholder="food, costiera"
                    data-testid="admin-diary-tags"
                  />
                </F>
                <F label="Stato pubblicazione">
                  <div className="h-11 flex items-center gap-3 px-4 rounded-xl border border-sand bg-white">
                    <Switch checked={!!edit.published} onCheckedChange={(v) => setEdit({ ...edit, published: v })} data-testid="admin-diary-published" />
                    <span className="text-sm text-ocean/70">{edit.published ? "Pubblicato" : "Bozza"}</span>
                  </div>
                </F>
                <F label="In evidenza">
                  <div className="h-11 flex items-center gap-3 px-4 rounded-xl border border-sand bg-white">
                    <Switch checked={!!edit.featured} onCheckedChange={(v) => setEdit({ ...edit, featured: v })} data-testid="admin-diary-featured" />
                    <span className="text-sm text-ocean/70">{edit.featured ? "Mostrato in alto" : "Articolo standard"}</span>
                  </div>
                </F>
              </div>

              <Tabs value={editLang} onValueChange={setEditLang}>
                <TabsList className="bg-white border border-sand">
                  {LANGS.map((lg) => (
                    <TabsTrigger key={lg} value={lg} data-testid={`admin-diary-tab-${lg}`}>{LANG_LABELS[lg]}</TabsTrigger>
                  ))}
                </TabsList>
                {LANGS.map((lg) => (
                  <TabsContent key={lg} value={lg} className="mt-5 space-y-4">
                    <F label="Titolo">
                      <input className={inputCls} value={edit.translations[lg].title} onChange={(e) => setTr(lg, "title", e.target.value)} data-testid={`admin-diary-title-${lg}`} />
                    </F>
                    <F label="Estratto (1-2 righe)">
                      <textarea rows={2} className={textCls} value={edit.translations[lg].excerpt} onChange={(e) => setTr(lg, "excerpt", e.target.value)} data-testid={`admin-diary-excerpt-${lg}`} />
                    </F>
                    <F label="Contenuto (HTML)">
                      <textarea rows={10} className={textCls} value={edit.translations[lg].content} onChange={(e) => setTr(lg, "content", e.target.value)} data-testid={`admin-diary-content-${lg}`} placeholder="<p>Testo dell'articolo...</p>" />
                    </F>
                  </TabsContent>
                ))}
              </Tabs>

              <div className="flex justify-end gap-3 pt-2 border-t border-sand">
                <button onClick={() => setEdit(null)} className="px-5 py-3 rounded-xl border border-sand text-sm text-ocean" data-testid="admin-diary-cancel">Annulla</button>
                <button onClick={save} disabled={saving} className="px-6 py-3 rounded-xl bg-ocean text-ivory text-sm font-medium disabled:opacity-60 btn-lift" data-testid="admin-diary-save">
                  {saving ? "Salvataggio..." : "Salva"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
